import { useMemo } from "react";
import { defineStepper } from "@stepperize/react";
import { Check, Ban, Minus, Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";

/**
 * Read-only / status stepper step.
 * Interactive wizards should use `defineStepper` from `@stepperize/react` directly
 * (re-exported via `@/shared/ui`) with Content + Actions.
 */
export type StatusStepperStepState =
  | "done"
  | "current"
  | "upcoming"
  | "skipped"
  | "blocked"
  | string;

export type StatusStepperStep = {
  id: string;
  label: string;
  state: StatusStepperStepState;
  /** Optional timestamp or status hint (e.g. '06:15 28/09' or 'Đang thực hiện'). */
  time?: string;
};

export type StatusStepperProps = {
  steps: StatusStepperStep[];
  "aria-label"?: string;
  className?: string;
  /** Compact height (~56–72px) for detail decision headers. */
  density?: "default" | "compact";
};

function resolveCurrentId(steps: StatusStepperStep[]): string {
  const current = steps.find(
    (s) =>
      s.state === "current" ||
      s.state === "in_progress" ||
      s.state === "inprogress" ||
      s.state === "running" ||
      s.state === "blocked",
  );
  if (current) return current.id;
  const lastDone = [...steps]
    .reverse()
    .find((s) => s.state === "done" || s.state === "completed");
  if (lastDone) return lastDone.id;
  return steps[0]!.id;
}

function indicatorClass(state: string, compact?: boolean): string {
  switch (state) {
    case "done":
    case "completed":
      return "border-success bg-success text-on-primary";
    case "current":
    case "in_progress":
    case "inprogress":
    case "running":
      return compact
        ? "border-blue-600 bg-blue-600 text-white ring-2 ring-blue-500/25 shadow-xs"
        : "border-blue-600 bg-blue-600 text-white ring-4 ring-blue-500/20 shadow-xs";
    case "blocked":
      return compact
        ? "border-hold bg-hold text-on-primary ring-2 ring-hold/25"
        : "border-hold bg-hold text-on-primary ring-4 ring-hold/25";
    case "skipped":
      return "border-border border-dashed bg-bg text-text-muted";
    case "upcoming":
    default:
      return "border-border bg-surface text-text-secondary";
  }
}

function connectorClass(prevState: string): string {
  if (prevState === "done" || prevState === "completed") return "bg-success";
  if (prevState === "skipped") return "bg-border-subtle";
  return "bg-border";
}

function titleClass(state: string): string {
  switch (state) {
    case "done":
    case "completed":
      return "text-success-fg font-medium";
    case "current":
    case "in_progress":
    case "inprogress":
    case "running":
      return "text-blue-700 font-semibold";
    case "blocked":
      return "text-hold-fg font-semibold";
    case "skipped":
      return "text-text-muted line-through decoration-border";
    default:
      return "text-text-secondary";
  }
}

type StepDef = { id: string; title: string };

/**
 * Classic horizontal status stepper (indicator · connector · title).
 * Radix has no Stepper — uses `@stepperize/react` primitives.
 */
export function StatusStepper({
  steps,
  "aria-label": ariaLabel = "Tiến trình",
  className,
  density = "default",
}: StatusStepperProps) {
  const defKey = steps.map((s) => `${s.id}\0${s.label}`).join("|");
  const compact = density === "compact";

  const definition = useMemo(() => {
    if (steps.length === 0) return null;
    const defs = steps.map((s): StepDef => ({ id: s.id, title: s.label }));
    return defineStepper(defs as [StepDef, ...StepDef[]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity from defKey
  }, [defKey]);

  if (!definition || steps.length === 0) return null;

  const stateById = new Map(steps.map((s) => [s.id, s.state]));
  const currentId = resolveCurrentId(steps);
  const completed = steps
    .filter((s) => s.state === "done" || s.state === "completed")
    .map((s) => s.id);
  const { Stepper } = definition;
  const lastIndex = steps.length - 1;

  return (
    <Stepper.Root
      step={currentId}
      completed={completed}
      beforeStepChange={() => false}
      orientation="horizontal"
      className={cn("w-full min-w-0", className)}
      aria-label={ariaLabel}
    >
      <div
        className={cn(
          "w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain px-0.5",
          compact ? "py-1" : "pt-2.5 pb-0.5",
        )}
      >
        <Stepper.List
          orientation="horizontal"
          className={cn(
            "m-0 flex list-none items-start gap-0 p-0",
            // Wide enough to read labels; scroll stays inside parent (min-w-0 + overflow-x-auto).
            compact ? "w-max min-w-[28rem]" : "w-max min-w-[36rem]",
            "sm:w-full sm:min-w-0",
          )}
        >
          <Stepper.Items>
            {(step, index) => {
              const state = stateById.get(step.id) ?? "upcoming";
              const prevState =
                index > 0
                  ? (stateById.get(steps[index - 1]!.id) ?? "upcoming")
                  : null;
              const isDone = state === "done" || state === "completed";
              const isCurrent =
                state === "current" ||
                state === "in_progress" ||
                state === "inprogress" ||
                state === "running";
              const current = isCurrent || state === "blocked";

              return (
                <Stepper.Item
                  key={step.id}
                  step={step.id}
                  className="flex min-w-0 flex-1 flex-col items-stretch"
                >
                  <div className={cn("flex w-full items-center", compact ? "h-6" : "h-8")}>
                    <div
                      className={cn(
                        "h-0.5 flex-1",
                        index === 0 ? "bg-transparent" : connectorClass(prevState!),
                        prevState === "skipped" && "opacity-50",
                      )}
                      aria-hidden
                    />
                    <Stepper.Trigger
                      disabled
                      className="relative z-[1] shrink-0 cursor-default border-0 bg-transparent p-0 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2"
                      aria-current={current ? "step" : undefined}
                    >
                      <Stepper.Indicator
                        className={cn(
                          "inline-flex shrink-0 items-center justify-center rounded-full border-2 font-semibold tabular-nums leading-none select-none",
                          compact
                            ? "size-5 text-[10px]"
                            : "size-7 text-caption",
                          indicatorClass(state, compact),
                        )}
                      >
                        {isDone ? (
                          <Check
                            className={cn(compact ? "size-2.5" : "size-3.5", "stroke-[2.5]")}
                            aria-hidden
                          />
                        ) : isCurrent ? (
                          <Loader2
                            className={cn(compact ? "size-2.5" : "size-3.5", "animate-spin")}
                            aria-hidden
                          />
                        ) : state === "blocked" ? (
                          <Ban className={compact ? "size-2.5" : "size-3.5"} aria-hidden />
                        ) : state === "skipped" ? (
                          <Minus className={compact ? "size-2.5" : "size-3.5"} aria-hidden />
                        ) : (
                          <span aria-hidden className="leading-none">{index + 1}</span>
                        )}
                      </Stepper.Indicator>
                    </Stepper.Trigger>
                    <div
                      className={cn(
                        "h-0.5 flex-1",
                        index === lastIndex
                          ? "bg-transparent"
                          : isDone
                            ? "bg-success"
                            : "bg-border",
                        state === "skipped" && index < lastIndex && "opacity-50",
                      )}
                      aria-hidden
                    />
                  </div>
                  <Stepper.Title
                    className={cn(
                      "m-0 px-1 text-center leading-snug whitespace-normal",
                      compact ? "mt-1 text-[11px] leading-tight" : "mt-2 text-caption",
                      titleClass(state),
                    )}
                  >
                    {step.title}
                  </Stepper.Title>
                  {steps[index]?.time ? (
                    <span
                      className={cn(
                        "mt-0.5 px-0.5 text-center text-[10px] leading-tight block",
                        isCurrent
                          ? "text-blue-700 font-semibold"
                          : "text-text-muted",
                      )}
                    >
                      {steps[index]?.time}
                    </span>
                  ) : null}
                </Stepper.Item>
              );
            }}
          </Stepper.Items>
        </Stepper.List>
      </div>
    </Stepper.Root>
  );
}
